import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, PageBody, Section } from "../components/layout/Page";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { name: "", code: "", address: "", gst: "", pan: "", email: "", phone: "" };

export default function Companies() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/companies");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      if (editingId) {
        await api.put(`/companies/${editingId}`, form);
        toast.success("Company updated");
      } else {
        await api.post("/companies", form);
        toast.success("Company created");
      }
      setOpen(false);
      setForm(empty);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const edit = (c) => {
    setForm({ name: c.name, code: c.code, address: c.address || "", gst: c.gst || "", pan: c.pan || "", email: c.email || "", phone: c.phone || "" });
    setEditingId(c.id);
    setOpen(true);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this company and all its data?")) return;
    await api.delete(`/companies/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="companies-page">
      <PageHeader eyebrow="Master data" title="Companies" subtitle="Manage the 5 businesses on the platform.">
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditingId(null); } }}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-xs uppercase tracking-wider" data-testid="add-company-btn">
              <Plus size={14} className="mr-1.5" /> New company
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader><DialogTitle>{editingId ? "Edit" : "New"} company</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label className="text-xs uppercase">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="company-name-input" /></div>
              <div><Label className="text-xs uppercase">Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} maxLength={5} className="rounded-sm font-mono" data-testid="company-code-input" /></div>
              <div><Label className="text-xs uppercase">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm" /></div>
              <div className="col-span-2"><Label className="text-xs uppercase">Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-sm" /></div>
              <div><Label className="text-xs uppercase">GST</Label><Input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} className="rounded-sm" /></div>
              <div><Label className="text-xs uppercase">PAN</Label><Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value })} className="rounded-sm" /></div>
              <div className="col-span-2"><Label className="text-xs uppercase">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm" /></div>
            </div>
            <DialogFooter><Button onClick={submit} className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="save-company-btn">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>
      <PageBody>
        <Section title={`${items.length} companies`} dense>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Address</TableHead>
                <TableHead>GST</TableHead><TableHead>PAN</TableHead><TableHead>Employees</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id} data-testid={`company-row-${c.code}`}>
                  <TableCell className="font-mono text-xs">{c.code}</TableCell>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="text-xs text-[#475569]">{c.address}</TableCell>
                  <TableCell className="text-xs font-mono">{c.gst}</TableCell>
                  <TableCell className="text-xs font-mono">{c.pan}</TableCell>
                  <TableCell>{c.employee_counter}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <button onClick={() => edit(c)} className="p-1.5 hover:bg-[#EFF6FF] rounded-sm" data-testid={`edit-${c.code}`}><PencilSimple size={14} /></button>
                    <button onClick={() => del(c.id)} className="p-1.5 hover:bg-[#F54236]/10 text-[#F54236] rounded-sm" data-testid={`delete-${c.code}`}><Trash size={14} /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      </PageBody>
    </div>
  );
}
